import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { OrderService } from '../../../../../services/customer-orders/order.service';
import { PST_NOTE_CATEGORY_ID } from '../../../../../models/patients/note';
import { Subscription } from 'rxjs';
import { AuthService } from '../../../../auth/auth.service';

@Component({
	selector: 'app-add-order-note',
	templateUrl: './add-order-note.component.html',
	styleUrls: ['./add-order-note.component.scss']
})
export class AddOrderNoteComponent implements OnInit, OnDestroy {
	noteForm!: FormGroup;
	categories: { id: number; name: string }[] = [];
	subjectOptions: string[] = [];
	dispatcherMode: boolean = false;
	showAutoApplyToOrderComment = false;
	readonly canAutoApplyToOrderComment: boolean;

	private readonly noteSubjectMap: Record<number, string[]> = {
		// DOVE-2667 — Refused, Family Reschedule, PST Reschedule, Item Not on Truck and Patient Profile
		// Update are retired (`retired: true` on the notesubject dropdown): not offered for new notes, but
		// existing notes keep and still display them. Item Exceptions and Patient Info Update are written
		// only by the system, so they are not offered either.
		4: ['Basic Note'],
		8: ['Cancel Order', 'Order Status Inquiry', 'Reschedule Order', 'Update Existing Order'],
		9: ['Update Patient Information']
	};

	private categorySub?: Subscription;

	constructor(
		private fb: FormBuilder,
		private dialogRef: DynamicDialogRef,
		private config: DynamicDialogConfig,
		private orderService: OrderService,
		private authService: AuthService
	) {
		this.canAutoApplyToOrderComment = this.authService.hasPermission('note.auto-apply-order-comment');
	}

	ngOnInit(): void {
		this.dispatcherMode = !!this.config.data?.dispatcherMode;
		this.showAutoApplyToOrderComment = !!this.config.data?.showAutoApplyToOrderComment;
		this.initForm();
		this.loadCategories();
		this.categorySub = this.noteForm.get('categoryId')!.valueChanges.subscribe((categoryId) => {
			this.subjectOptions = categoryId ? (this.noteSubjectMap[categoryId] ?? []) : [];
			this.noteForm.get('subject')!.setValue('');
		});

		if (this.dispatcherMode) {
			this.noteForm.get('categoryId')!.setValue(PST_NOTE_CATEGORY_ID);
			this.noteForm.get('categoryId')!.disable();
		}
	}

	ngOnDestroy(): void {
		this.categorySub?.unsubscribe();
	}

	private initForm() {
		this.noteForm = this.fb.group({
			critical: [false],
			patientPopup: [false],
			orderPopup: [false],
			subject: ['', Validators.required],
			note: ['', Validators.required],
			categoryId: [null, Validators.required]
		});

		if (this.showAutoApplyToOrderComment) {
			this.noteForm.addControl(
				'autoApplyToOrderComment',
				this.fb.control({ value: false, disabled: !this.canAutoApplyToOrderComment })
			);
		}
	}

	loadCategories() {
		this.orderService.getOrderNoteCategories().subscribe((categories) => {
			// Profiles can't create PST notes; dispatcher notes are legitimately PST.
			this.categories = this.dispatcherMode
				? categories
				: categories.filter((category) => category.id !== PST_NOTE_CATEGORY_ID);
		});
	}

	get hasPresetSubjects(): boolean {
		return this.subjectOptions.length > 0;
	}

	onCancel(): void {
		this.dialogRef.close();
	}

	onAdd(): void {
		if (this.noteForm.valid) {
			this.dialogRef.close(this.noteForm.getRawValue());
		} else {
			this.noteForm.markAllAsTouched();
		}
	}
}
